import { z } from '../zod.js';
/**
 * Query-string fields. A query arrives as strings and both `bind` and the mock read one value
 * per key, so numbers and booleans are parsed from their text and a list travels as one
 * comma-separated value.
 */
/** The text of a query integer: digits with an optional leading minus. */
export const DECIMAL_INTEGER_PATTERN = /^-?[0-9]+$/;
/**
 * An integer from its decimal text: digits with an optional leading minus. Hex (`0x10`), an
 * exponent (`1e1`), a fraction (`1.5`), a sign other than a leading minus, spaces and blank text
 * are refused, so `bind` answers them 400 `invalid_request` (1.0b deliverable 16b). A caller may
 * pass the integer as a number, which the query string turns into text (as `QueryBoolean` takes a
 * boolean), so the output stays a number. The number branch carries the bounds into the JSON
 * Schema snapshot.
 */
export function queryInt(min, max) {
    const int = z.number().int().min(min).max(max);
    return z.union([
        int,
        z.string().regex(DECIMAL_INTEGER_PATTERN).pipe(z.coerce.number().pipe(int)),
    ]);
}
/**
 * A boolean, or its text (`true`, `false`, `1`, `0`, `yes`, `no`): a caller may pass the boolean,
 * which the query string turns into text.
 */
export const QueryBoolean = z.union([z.boolean(), z.stringbool()]);
/**
 * A strict query boolean (CS r2c, `placements.list`'s `participating`): only the text `true` or
 * `false`, or the boolean itself (a caller may pass it, as `queryInt` takes a number). `yes`, `1`,
 * `TRUE` and blank text are refused, so `bind` answers them 400 `invalid_request`.
 */
export function queryBool() {
    return z.union([z.boolean(), z.enum(['true', 'false']).transform((text) => text === 'true')]);
}
/**
 * A comma-separated list of items matching `item` (a pattern without anchors), at most `max`.
 * It stays a string: the reader splits it on commas.
 */
export function commaList(item, max) {
    return z.string().regex(new RegExp(`^(?:${item})(?:,(?:${item})){0,${max - 1}}$`));
}
/** Split a value of {@link commaList}. */
export function splitCommaList(value) {
    return value.split(',');
}
/** `?baseRv=` of a DELETE on a row that carries `rv` (1.0b conventions, "Writes"). */
export const BaseRvQueryV1 = z.strictObject({ baseRv: queryInt(1, 2_147_483_647) });
//# sourceMappingURL=query.js.map